import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ClipLoader } from "react-spinners";
import {
  Search,
  Filter,
  ShoppingBag,
  Clock,
  CreditCard,
  MapPin,
  Star,
  Heart,
  Share2,
  Eye,
  ShoppingCart,
  X,
  ChevronDown,
  RefreshCcw,
  TrendingUp,
  Package,
  DollarSign,
  Award,
  ArrowRight,
} from "lucide-react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addOrder } from "../redux/ordersSlice";
import { PRODUCT_API, BASE_API_URL } from "../data/api";
import { useThemeAPI } from "../context/ThemeContextAPI";
import ThemeSelector from "../components/ThemeSelector";
import CartPopup from "./CartPopup";

// Animated Product Card
function ProductCard({ product, onBuy, onView, index }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      whileHover={{ y: -8, scale: 1.02 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100 group relative"
    >
      {/* Wishlist Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsWishlisted(!isWishlisted);
          toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist");
        }}
        className={`absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
          isWishlisted
            ? "bg-red-500 text-white shadow-lg shadow-red-500/30"
            : "bg-white/90 backdrop-blur text-gray-400 hover:text-red-500 shadow-md"
        }`}
      >
        <Heart size={20} fill={isWishlisted ? "currentColor" : "none"} />
      </button>

      {/* Image Container */}
      <div className="relative h-56 overflow-hidden bg-gray-100">
        <motion.img
          src={product.imageUrl || "https://via.placeholder.com/400x300?text=No+Image"}
          alt={product.name}
          className="w-full h-full object-cover"
          animate={{ scale: isHovered ? 1.1 : 1 }}
          transition={{ duration: 0.5 }}
        />
        
        {/* Quick Actions Overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 flex items-center justify-center gap-3"
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onView(product);
                }}
                className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg"
              >
                <Eye size={20} className="text-gray-700" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onBuy(product);
                }}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30"
              >
                <ShoppingCart size={20} className="text-white" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Discount Badge */}
        {product.discountPercent > 0 && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
            {product.discountPercent}% OFF
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Category Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg">
            {product.type}
          </span>
          {product.isCustomizable && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg">
              Customizable
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-1 group-hover:text-blue-600 transition-colors">
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">
          {product.shortDescription}
        </p>

        {/* Specifications */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Package size={14} className="text-blue-500" />
            <span>{product.capacityKG} kg</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <TrendingUp size={14} className="text-green-500" />
            <span>{product.speedMPS} m/s</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Award size={14} className="text-purple-500" />
            <span>{product.warrantyYears} Yrs</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Star size={14} className="text-yellow-500" />
            <span>4.8 ★</span>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-end justify-between mb-4">
          <div>
            {product.discountPercent > 0 ? (
              <>
                <p className="text-sm text-gray-400 line-through">
                  ₹{product.price.toLocaleString()}
                </p>
                <p className="text-2xl font-bold text-gray-800">
                  ₹{Math.round(product.price * (1 - product.discountPercent / 100)).toLocaleString()}
                </p>
              </>
            ) : (
              <p className="text-2xl font-bold text-gray-800">
                ₹{product.price.toLocaleString()}
              </p>
            )}
          </div>
          <span className="text-xs text-green-600 font-medium">
            {product.status}
          </span>
        </div>

        {/* Buy Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.stopPropagation();
            onBuy(product);
          }}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <ShoppingCart size={18} />
          Buy Now
        </motion.button>
      </div>
    </motion.div>
  );
}

// Filter Sidebar
function FilterSidebar({ priceRanges, priceFilters, togglePriceFilter, onReset, products, onProductClick }) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    return products.filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm]);

  return (
    <motion.div
      initial={{ x: 50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="w-80 bg-white rounded-3xl shadow-xl p-6 h-fit sticky top-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
            <Filter className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Filters</h2>
        </div>
        <button
          onClick={onReset}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors"
        >
          <RefreshCcw size={14} />
          Reset
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <label className="text-sm font-medium text-gray-700 mb-2 block">Search Products</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border border-transparent focus:bg-white focus:border-blue-500 rounded-xl outline-none transition-all text-sm"
          />
        </div>
      </div>

      {/* Price Filter */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <DollarSign size={16} className="text-green-500" />
          Price Range
        </h3>
        <div className="space-y-2">
          {priceRanges.map((range) => (
            <label
              key={range.id}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                priceFilters.includes(range.id)
                  ? "bg-blue-50 border border-blue-200"
                  : "bg-gray-50 hover:bg-gray-100 border border-transparent"
              }`}
            >
              <input
                type="checkbox"
                checked={priceFilters.includes(range.id)}
                onChange={() => togglePriceFilter(range.id)}
                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{range.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Quick Product List */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Package size={16} className="text-purple-500" />
          Quick Access
        </h3>
        <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
          {filteredProducts.slice(0, 10).map((product) => (
            <button
              key={product.id}
              onClick={() => onProductClick(product)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <img
                src={product.imageUrl || "https://via.placeholder.com/40"}
                alt={product.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{product.name}</p>
                <p className="text-xs text-gray-500">₹{product.price.toLocaleString()}</p>
              </div>
              <ArrowRight size={14} className="text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// Orders Modal
function OrdersModal({ show, onClose, orders }) {
  const getRemainingTime = (placedAt) => {
    const now = new Date();
    const endTime = new Date(placedAt);
    endTime.setDate(endTime.getDate() + 7);
    const diff = endTime - now;

    if (diff <= 0) return "Expired";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / 1000 / 60) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered": return "bg-green-100 text-green-700";
      case "processing": return "bg-blue-100 text-blue-700";
      case "shipped": return "bg-purple-100 text-purple-700";
      default: return "bg-yellow-100 text-yellow-700";
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            className="bg-white w-full max-w-4xl max-h-[85vh] rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">My Orders</h2>
                <p className="text-white/80 text-sm">{orders.length} orders placed</p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <ShoppingBag className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Orders Yet</h3>
                  <p className="text-gray-500 mb-6">Start shopping to see your orders here</p>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium"
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, index) => (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 rounded-2xl p-5 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={order.thumbnail || "https://via.placeholder.com/80"}
                          alt={order.item}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-800">{order.item}</h4>
                              <p className="text-sm text-gray-500">Order #{order.id}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                            <div>
                              <p className="text-gray-500">Amount</p>
                              <p className="font-semibold text-gray-800">{order.amount}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Qty</p>
                              <p className="font-semibold text-gray-800">{order.qty}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Payment</p>
                              <p className="font-semibold text-gray-800">{order.paymentMethod}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Time Left</p>
                              <p className="font-semibold text-orange-600">{getRemainingTime(order.placedAt)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Stats Card
function StatsCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`p-4 rounded-2xl bg-white shadow-lg border border-gray-100`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

const Dashboard = () => {
  const [showOrders, setShowOrders] = useState(false);
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [timer, setTimer] = useState(0);
  const [priceFilters, setPriceFilters] = useState([]);

  const orders = useSelector((state) => state.orders.list);
  const subscription = useSelector((state) => state.subscription);
  const searchText = useSelector((state) => state.productFilter?.search) || "";
  const selectedCategory = useSelector((state) => state.productFilter?.category) || "All Products";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme, setTheme, loading: themeLoading } = useThemeAPI();

  const priceRanges = [
    { id: 1, label: "Under ₹10,000", min: 0, max: 10000 },
    { id: 2, label: "₹10,000 - ₹50,000", min: 10000, max: 50000 },
    { id: 3, label: "₹50,000 - ₹1,00,000", min: 50000, max: 100000 },
    { id: 4, label: "Above ₹1,00,000", min: 100000, max: Infinity },
  ];

  const getFinalPrice = (price, discountPercent = 0) => {
    const discount = subscription.discount || discountPercent || 0;
    return Math.round(price - (price * discount) / 100);
  };

  const togglePriceFilter = (id) => {
    setPriceFilters((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetFilters = () => {
    setPriceFilters([]);
    setFiltered(products);
  };

  const handlePaymentComplete = async (product, qty, paymentMethod, address) => {
    const finalAmount = getFinalPrice(product.price, product.discountPercent) * qty;
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.userId;

    if (!userId) {
      toast.error("User not logged in!");
      return;
    }

    try {
      const balanceRes = await axios.get(`${BASE_API_URL}/Wallet/${userId}`);
      const currentBalance = balanceRes.data.balance || 0;

      if (currentBalance < finalAmount) {
        toast.error("Insufficient wallet balance!");
        return;
      }

      await axios.post(
        `${BASE_API_URL}/Wallet/spend`,
        { userId, amount: finalAmount, note: `Purchase: ${product.name} x ${qty}` },
        { headers: { "Content-Type": "application/json" } }
      );

      dispatch(
        addOrder({
          id: `ORD${Date.now()}`,
          item: product.name,
          amount: `₹${finalAmount.toLocaleString()}`,
          status: "Processing",
          qty,
          paymentMethod: "Wallet",
          address,
          placedAt: new Date().toISOString(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString(),
          thumbnail: product.imageUrl,
        })
      );

      setSelectedProduct(null);
      setShowOrders(true);
      toast.success("Purchase successful! Admin will contact you within 7 days.");
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Something went wrong!");
    }
  };

  // Timer
  useEffect(() => {
    const interval = setInterval(() => setTimer((prev) => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await axios.get(PRODUCT_API.GET_ALL);
        const data = response.data;

        const transformed = data.map((item) => ({
          id: item.productId,
          name: item.productName || "Unnamed Product",
          price: item.price || 0,
          imageUrl: item.images?.[0]?.imageURL && item.images[0].imageURL !== "string"
            ? `${BASE_API_URL.replace("/api", "")}/uploads/${encodeURIComponent(item.images[0].imageURL)}`
            : null,
          shortDescription: item.description || "No description",
          discountPercent: item.discountPercent || 0,
          type: item.productType || "General",
          quantity: item.quantity || 0,
          status: item.status || "Available",
          capacityKG: item.capacityKG || 0,
          speedMPS: item.speedMPS || 0,
          machineRoomType: item.machineRoomType || "N/A",
          doorType: item.doorType || "N/A",
          floorsSupported: item.floorsSupported || 0,
          warrantyYears: item.warrantyYears || 0,
          powerConsumption: item.powerConsumption || "N/A",
          features: item.features || "No features",
          isCustomizable: item.isCustomizable || false,
        }));

        setProducts(transformed);
        setFiltered(transformed);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...products];

    if (searchText) {
      result = result.filter((p) =>
        p.name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (selectedCategory && selectedCategory !== "All Products") {
      result = result.filter(
        (p) => p.type === selectedCategory || p.name === selectedCategory
      );
    }

    if (priceFilters.length > 0) {
      result = result.filter((p) =>
        priceFilters.some((id) => {
          const range = priceRanges.find((r) => r.id === id);
          return range && p.price >= range.min && p.price <= range.max;
        })
      );
    }

    setFiltered(result);
  }, [searchText, selectedCategory, priceFilters, products]);

  if (themeLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <ClipLoader size={50} color="#2563eb" />
          <p className="mt-4 text-blue-700 font-medium">Loading theme...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-screen bg-gray-50 ${theme}`}>
      {/* LEFT - Products Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              <ShoppingBag className="text-blue-600" />
              Elevators
              {!loadingProducts && (
                <span className="text-sm font-normal text-gray-500">({filtered.length} products)</span>
              )}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowOrders(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30"
            >
              <Package size={20} />
              My Orders
            </motion.button>
          </div>
        </header>

        {/* Stats */}
        {/* <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard icon={Package} label="Total Products" value={products.length} color="from-blue-500 to-cyan-500" delay={0} />
          <StatsCard icon={ShoppingBag} label="My Orders" value={orders.length} color="from-purple-500 to-pink-500" delay={0.1} />
          <StatsCard icon={DollarSign} label="Cart Items" value={0} color="from-green-500 to-emerald-500" delay={0.2} />
          <StatsCard icon={Award} label="Subscription" value={subscription.plan || "Free"} color="from-orange-500 to-red-500" delay={0.3} />
        </div> */}

        {/* Product Grid */}
        <main className="flex-1 overflow-y-auto p-6">
          {loadingProducts ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <ClipLoader color="#2563eb" size={60} />
              <p className="text-blue-700 font-medium animate-pulse">Loading products...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                <Search className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">No Products Found</h3>
              <p className="text-gray-500">Try adjusting your filters</p>
              <button
                onClick={resetFilters}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  onBuy={setSelectedProduct}
                  onView={(p) => navigate(`/product-page/${p.id}`)}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* RIGHT - Filter Sidebar */}
      <FilterSidebar
        priceRanges={priceRanges}
        priceFilters={priceFilters}
        togglePriceFilter={togglePriceFilter}
        onReset={resetFilters}
        products={products}
        onProductClick={(p) => navigate(`/product-page/${p.id}`)}
      />

      {/* Theme Selector */}
      <ThemeSelector currentTheme={theme} setTheme={setTheme} />

      {/* Orders Modal */}
      <OrdersModal show={showOrders} onClose={() => setShowOrders(false)} orders={orders} />

      {/* Cart Popup */}
      <AnimatePresence>
        {selectedProduct && (
          <CartPopup
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onComplete={handlePaymentComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
